import { FC } from "react";
import {
  Box,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Alert,
  useTheme
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { types, Button, toast } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";

interface PrivateMileageModalProps {
  open: boolean;
  onClose: () => void;
  ride: types.IObjectRide | null;
  onSubmit: (values: PrivateMileageFormValues) => void;
}

export interface PrivateMileageFormValues {
  privateMileage: string;
  changeReason: string;
}

const PrivateMileageModal: FC<PrivateMileageModalProps> = ({
  open,
  onClose,
  ride,
  onSubmit
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const currentRideMileage = ride?.mileage || 0;
  const existingPrivateMileage = (ride as any)?.privateMileage || 0;
  const existingPrivateReason = (ride as any)?.privateReason || "";

  const maxAllowedMileage = currentRideMileage;

  const validationSchema = (t: any) =>
    Yup.object({
      privateMileage: Yup.number()
        .required(t("validation.fieldRequired"))
        .min(0, t("validation.cannotBeNegative"))
        .max(
          maxAllowedMileage,
          t("validation.privateMileageExceedsRideMileage", {
            mileage: currentRideMileage.toLocaleString()
          })
        )
        .typeError(t("validation.mustBeNumber")),
      changeReason: Yup.string()
        .trim()
        .min(1, t("validation.fieldRequired"))
        .required(t("validation.fieldRequired"))
    });

  const hasExistingData =
    existingPrivateMileage > 0 || existingPrivateReason !== "";

  const initialValues: PrivateMileageFormValues = {
    privateMileage: existingPrivateMileage
      ? String(existingPrivateMileage)
      : "",
    changeReason: existingPrivateReason || ""
  };

  const formatRideDate = (ride: types.IObjectRide | null) => {
    if (!ride?.startDate || !ride?.endDate) return "";

    const startDate = new Date(ride.startDate);
    const endDate = new Date(ride.endDate);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];
    const month = months[startDate.getMonth()];
    const day = startDate.getDate();
    const year = startDate.getFullYear();

    const daySuffix = (d: number) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    const formatTime = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    const dateStr = `${month} ${day}${daySuffix(day)}, ${year}`;
    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);

    return `${dateStr}, ${startTime} - ${endTime}`;
  };

  const handleSubmit = async (
    values: PrivateMileageFormValues,
    formikHelpers: FormikHelpers<PrivateMileageFormValues>
  ) => {
    // Check if values are the same as existing data
    const isSameData =
      hasExistingData &&
      Number(values.privateMileage) === existingPrivateMileage &&
      values.changeReason === existingPrivateReason;

    if (isSameData) {
      toast.error(t("messages.noChangesDetected"));
      formikHelpers.setSubmitting(false);
      return;
    }

    try {
      await onSubmit(values);
      onClose();
    } catch (error) {
      console.error("Error submitting private mileage:", error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "400px",
          height: "100vh",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            paddingTop: 3,
            paddingBottom: 1,
            paddingX: 3,
            borderBottom: "1px solid #E0E0E0"
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "24px",
                color: "#323947"
              }}
            >
              {t("titles.privateMileageForRide")} <br />
              {formatRideDate(ride)}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ mt: -1, mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Form Content */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldTouched,
            setFieldValue,
            isSubmitting,
            isValid
          }) => {
            const hasChanges =
              Number(values.privateMileage) !== existingPrivateMileage ||
              values.changeReason !== existingPrivateReason;

            const isFormValid = isValid && hasChanges;

            return (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%"
                }}
              >
                <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
                  {/* Private Mileage (km) */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {t("titles.privateMileage")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="privateMileage"
                      type="number"
                      placeholder="e.g. 3500"
                      value={values.privateMileage}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const numericValue = Number(inputValue);

                        if (numericValue > maxAllowedMileage) {
                          const correctedValue = maxAllowedMileage.toFixed(1);
                          setFieldValue("privateMileage", correctedValue);
                        } else {
                          handleChange(e);
                        }
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        setFieldTouched("privateMileage", true);

                        if (e.target.value === "" || e.target.value === null) {
                          setFieldValue("privateMileage", "0");
                        }
                      }}
                      error={
                        touched.privateMileage && Boolean(errors.privateMileage)
                      }
                      helperText={
                        touched.privateMileage && errors.privateMileage
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px"
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider
                        },
                        "& .MuiOutlinedInput-input": {
                          fontSize: "14px",
                          padding: "12px"
                        }
                      }}
                    />
                  </Box>

                  {/* Change Reason */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {t("titles.changeReason")}
                      <Typography
                        component="span"
                        sx={{ color: "error.main", ml: 0.5 }}
                      >
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      fullWidth
                      name="changeReason"
                      placeholder={t(
                        "titles.descriptionOfTheChangeOfPrivateMileage"
                      )}
                      value={values.changeReason}
                      multiline
                      rows={2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.changeReason && Boolean(errors.changeReason)
                      }
                      helperText={touched.changeReason && errors.changeReason}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px"
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider
                        },
                        "& .MuiOutlinedInput-input": {
                          fontSize: "14px"
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Warning Box */}
                <Box sx={{ px: 3, pb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      border: `1px solid ${theme.palette.warning.light}`,
                      borderRadius: "8px",
                      backgroundColor: `${theme.palette.warning.light}20`
                    }}
                  >
                    <WarningAmberIcon
                      sx={{
                        color: theme.palette.warning.light,
                        fontSize: "24px",
                        flexShrink: 0
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#323947",
                        lineHeight: "20px"
                      }}
                    >
                      {t("titles.theRideTypeWillChangeToMixed")}
                    </Typography>
                  </Box>
                </Box>

                {/* Footer Actions */}
                <Box
                  sx={{
                    p: 3,
                    borderTop: "1px solid #E0E0E0",
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end"
                  }}
                >
                  <Button
                    onClick={onClose}
                    disabled={isSubmitting}
                    variant="text"
                    text={t("buttonsText.cancel")}
                    sx={{
                      color: theme.palette.secondary.main
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !isFormValid}
                    text={t("buttonsText.save")}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.contrastText
                      }
                    }}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Drawer>
  );
};

export default PrivateMileageModal;
